import React, { useState, useEffect } from 'react';
import { Table, Button, message, Space, Upload, Card, Typography } from 'antd';
import { UploadOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title } = Typography;

const CourierManagement = () => {
  const [couriers, setCouriers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourier, setSelectedCourier] = useState(null);
  const [verificationResults, setVerificationResults] = useState([]);
  const [uploading, setUploading] = useState(false);

  const fetchCouriers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/admin/couriers/');
      setCouriers(response.data);
    } catch (error) {
      message.error('获取快递公司数据失败');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCouriers();
  }, []);

  const handleBillVerification = (courier) => {
    setSelectedCourier(courier);
    setVerificationResults([]);
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    setVerificationResults([]);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`/api/admin/couriers/${selectedCourier.id}/verify-bill/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.results) {
        setVerificationResults(response.data.results);
        message.success('账单核对完成');
      } else {
        message.error('账单格式错误或无数据');
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error(error.response?.data?.error || '账单核对失败');
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleFile = () => {
    window.open('/api/admin/couriers/sample-bill/', '_blank');
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/admin/couriers/${id}/`);
      message.success('删除成功');
      fetchCouriers();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: 'Logo',
      dataIndex: 'logo',
      key: 'logo',
      render: (logo) => logo ? (
        <img 
          src={logo} 
          alt="快递公司logo" 
          style={{ width: '80px', height: '40px', objectFit: 'contain' }}
        />
      ) : null,
    },
    {
      title: '快递公司名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '快递公司代码',
      dataIndex: 'code',
      key: 'code',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" onClick={() => handleBillVerification(record)}>
            账单核对
          </Button>
        </Space>
      ),
    },
  ];

  if (selectedCourier) {
    return (
      <div>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => setSelectedCourier(null)}
            >
              返回
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              {selectedCourier.name} - 账单核对
            </Title>
          </Space>

          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Upload
                  accept=".xlsx,.xls"
                  maxCount={1}
                  showUploadList={true}
                  beforeUpload={(file) => {
                    handleFileUpload(file);
                    return false;
                  }}
                  onRemove={() => setVerificationResults([])}
                >
                  <Button 
                    icon={<UploadOutlined />} 
                    loading={uploading}
                    disabled={uploading}
                    type="primary"
                  >
                    {uploading ? '处理中...' : '选择账单文件'}
                  </Button>
                </Upload>
                <Button onClick={downloadSampleFile}>
                  下载模板
                </Button>
              </Space>
              
              {verificationResults.length > 0 && (
                <Table
                  dataSource={verificationResults}
                  columns={[
                    {
                      title: '运单号码',
                      dataIndex: 'express_number',
                      key: 'express_number',
                    },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      key: 'status',
                      render: (status) => (
                        <span style={{ color: status === 'mismatch' ? '#ff4d4f' : status === 'not_found' ? '#faad14' : '#52c41a' }}>
                          {status === 'mismatch' ? '重量不匹配' : status === 'not_found' ? '未找到订单' : '匹配'}
                        </span>
                      ),
                    },
                    {
                      title: '账单重量',
                      dataIndex: 'bill_weight',
                      key: 'bill_weight',
                    },
                    {
                      title: '系统重量',
                      dataIndex: 'system_weight',
                      key: 'system_weight',
                    },
                    {
                      title: '差异',
                      dataIndex: 'weight_difference',
                      key: 'weight_difference',
                      render: (diff) => diff ? <span style={{ color: '#ff4d4f' }}>{diff}</span> : '-',
                    }
                  ]}
                  rowKey="express_number"
                  pagination={false}
                  size="small"
                />
              )}
            </Space>
          </Card>
        </Space>
      </div>
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={couriers}
      rowKey="id"
      loading={loading}
      size="small"
    />
  );
};

export default CourierManagement;
